import React, {type ChangeEvent, forwardRef, type ReactNode, useEffect, useId, useState} from 'react'
import InputErrorLabel from '@/Components/Forms/InputErrorLabel'
import _ from 'lodash'
import InputMask from '@mona-health/react-input-mask'
import {Input} from '@/Types/Input'
import {formatPhoneForInput, normalizeToE164} from '@/Utils/Phone'

interface Props extends Input {
    prepend?: string
    type: 'text' | 'tel' | 'email' | 'password' | 'date' | 'number'
    placeholder?: string
    autofocus?: boolean
    mask?: string | undefined | RegExp[]
    beforeMaskedStateChange?: (state: {
        currentState: { value: string, selection: { end: number, length: 0, start: 0 } }
        nextState: { value: string, selection: { end: number, start: number } }
    }) => void
    maxlength?: number
    testId?: string
}

export default forwardRef<HTMLInputElement, Props>(function Input(
    {
        ariaLabel,
        title,
        description,
        prepend,
        type,
        value,
        field,
        setData,
        placeholder,
        error,
        autofocus,
        disabled,
        required,
        mask,
        beforeMaskedStateChange,
        maxlength,
        testId,
        className,
        inputClass,
        ...props
    }: Props, forwardedRef): ReactNode {
    const [currentValue, setCurrentValue] = useState<string | null>('')
    const descriptionId = useId()
    const hasDescription = description != null && description !== ''
    const errorId = error != null ? testId + '-error' : undefined
    const describedBy = [hasDescription ? descriptionId : null, errorId ?? null]
        .filter((id): id is string => id != null)
        .join(' ') || undefined

    useEffect(() => {
        const raw = !Array.isArray(value) ? value ?? '' : ''
        setCurrentValue(type === 'tel' && raw ? formatPhoneForInput(raw) : raw)
    }, [])

    const inputClasses = (): string => {
        let base = 'form-control'

        if (prepend != null) {
            base += ' with-prepend'
        }

        if (error != null) {
            base += ' has-error'
        }

        return `${base} ${inputClass ?? ''}`
    }
    const labelClasses = (): string => {
        let base: string = 'form-control-label'

        if (error != null) {
            base += ' has-error'
        }

        return base
    }
    const formattedTitle = (): string | null => {
        if (title != null) {
            return _.camelCase(title)
        }
        return null
    }

    return <div className={`block relative ${className ?? ''}`}>
        {(title != null)
            ? <label className={labelClasses()} htmlFor={field ?? formattedTitle() ?? ''}>{title}</label>
            : null}
        {hasDescription
            ? <p id={descriptionId} className={'mb-2 text-sm text-gray-500'}>{description}</p>
            : null}
        <div className={'relative'}>
            {type === 'tel'
                ? <input
                    type="tel"
                    autoComplete="tel"
                    name={field}
                    id={field ?? formattedTitle() ?? undefined}
                    data-testid={testId}
                    aria-label={ariaLabel}
                    placeholder={placeholder}
                    className={inputClasses()}
                    value={currentValue ?? ''}
                    disabled={disabled}
                    required={required}
                    aria-required={required || undefined}
                    aria-invalid={error != null || undefined}
                    aria-describedby={describedBy}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const raw = e.target.value
                        setCurrentValue(raw)
                        const e164 = normalizeToE164(raw)
                        setData?.(field ?? formattedTitle() ?? '', e164 ?? raw)
                    }}
                    onBlur={() => {
                        const e164 = normalizeToE164(currentValue ?? '')
                        if (e164) {
                            setCurrentValue(formatPhoneForInput(currentValue ?? ''))
                            setData?.(field ?? formattedTitle() ?? '', e164)
                        }
                    }}
                />
                : <InputMask
                    name={field}
                    id={field}
                    data-testid={testId}
                    ref={forwardedRef}
                    {...props}
                    mask={mask}
                    beforeMaskedStateChange={beforeMaskedStateChange}
                    type={type}
                    placeholder={placeholder}
                    className={inputClasses()}
                    aria-label={ariaLabel}
                    value={currentValue ?? ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        if (type === 'number') {
                            const numericValue: number = Number(e.target.value)
                            if (e.target.value.length !== 0 && numericValue < 0) {
                                setCurrentValue('0')
                                if (setData !== undefined) {
                                    setData(
                                        field ?? formattedTitle() ?? '',
                                        '0'
                                    )
                                }
                            } else {
                                setCurrentValue(e.target.value)
                                if (setData !== undefined) {
                                    setData(
                                        field ?? formattedTitle() ?? '',
                                        e.target.value
                                    )
                                }
                            }
                        } else {
                            setCurrentValue(e.target.value)
                            if (setData !== undefined) {
                                setData(
                                    field ?? formattedTitle() ?? '',
                                    e.target.value
                                )
                            }
                        }
                    }}
                    disabled={disabled}
                    required={required}
                    aria-required={required || undefined}
                    aria-invalid={error != null || undefined}
                    aria-describedby={describedBy}
                    autoFocus={autofocus}
                    maxLength={maxlength}
                />
            }
            {(prepend != null)
                ? <span className={'form-control-prepend'}>{prepend}</span>
                : null}
        </div>
        <InputErrorLabel error={error} id={testId + '-error'} testId={testId + '-error'}/>
    </div>
})
